/* Verbatim port of the asset-category config and the three SERP color axes
 * from service/review/index.html (~813-817, ~1101-1132).
 *
 * DEVIATION: `canEdit` (originally ~877, alongside factVal/factSrc/pct in
 * the generic-helpers block) is included here rather than in a helpers
 * file, because it is a one-line derivative of CLIENT_EDIT and is needed by
 * derive/exec.ts (footprint, threatItems) and derive/entity.ts
 * (planeVerdicts, via footprint). Verbatim body.
 *
 * DEVIATION: AXES.*.of()'s second parameter `asset` is unused in all three
 * implementations in the original too — but strict tsconfig's
 * `noUnusedParameters` rejects an unused, non-underscore-prefixed
 * parameter. Renamed to `_asset` (TS's documented exemption for
 * noUnusedParameters) to keep the signature's arity/position identical
 * while satisfying the compiler; this is a rename only, not a reshape.
 */
import { domainOf } from '../lib/format.ts';
import type { NormalizedAsset } from '../types/contract.ts';

// Intersection type: the four known keys read as plain `string` (the exec
// ledger builds labels from dot access), while arbitrary string indexing
// stays available for category values that arrive as bare strings.
type CatMap = Record<string, string> & { owned: string; editable: string; claimable: string; media: string };
export const CAT_COLOR: CatMap = { owned:'var(--own-c)', editable:'var(--edit-c)', claimable:'var(--claim-c)', media:'var(--unctrl-c)' };
export const CAT_LABEL: CatMap = { owned:'Owned', editable:'Editable (client)', claimable:'Claimable', media:'Not controlled' };
export const BAD_STATUS: string[] = ['abandoned','stale','unclaimed','inactive'];
export const CLIENT_EDIT: string[] = ['full','shared'];
export const PROFILE_TYPES: string[] = ['social-profile','video-channel','review-site','directory-listing','wiki','financial-profile','forum','careers-profile','regulatory-listing'];

/** Minimal structural param: every caller passes an asset-shaped record
 * (NormalizedAsset, or a duck-typed row carrying controlLevel). */
export function canEdit(a: { controlLevel?: string | null }): boolean { return CLIENT_EDIT.includes(a.controlLevel ?? ''); }

/** What the three axes actually read off a SERP row/element — SerpResult,
 * SerpElement and SerpFeatureItem are all structurally assignable. */
export interface SerpAxisInput {
  rating?: string | null;
  ownership?: string | null;
  url?: string | null;
  [k: string]: unknown;
}

export interface SerpAxis {
  label: string;
  legend: [string, string][];
  of(r: SerpAxisInput, _asset: NormalizedAsset | null): string[];
}

/* ── the three SERP color axes (firm vocabulary, verbatim) ── */
export const AXES: Record<'sentiment' | 'ownership' | 'authority', SerpAxis> = {
  sentiment: {
    label:'Sentiment', legend:[['Desirable','var(--ax-des)'],['Undesirable','var(--ax-undes)'],['Neutral','var(--ax-neu)'],['Unrated','var(--ax-unrated)']],
    of(r, _asset){
      if (r.rating === 'positive') return ['Desirable','var(--ax-des)'];
      if (r.rating === 'negative') return ['Undesirable','var(--ax-undes)'];
      if (r.rating === 'neutral')  return ['Neutral','var(--ax-neu)'];
      return ['Unrated','var(--ax-unrated)','lite'];
    }
  },
  ownership: {
    label:'Ownership', legend:[['Owned','var(--ax-owned)'],['Not Owned','var(--ax-notowned)'],['Not Me','var(--ax-notme)']],
    of(r, _asset){
      if (r.ownership === 'owned') return ['Owned','var(--ax-owned)'];
      if (r.ownership === 'resembler') return ['Not Me','var(--ax-notme)'];
      return ['Not Owned','var(--ax-notowned)'];
    }
  },
  authority: {
    label:'Authority type', legend:[['Owned','var(--ax-authowned)'],['Wikipedia','var(--ax-wiki)'],['Social','var(--ax-social)'],['Crowd','var(--ax-crowd)'],['Expert','var(--ax-expert)'],['Other','var(--ax-notowned)']],
    of(r, _asset){
      const d = (domainOf(r.url) || '').toLowerCase();
      if (r.ownership === 'owned') return ['Owned','var(--ax-authowned)'];
      if (/wikipedia\.org|wikidata\.org|wikimedia/.test(d)) return ['Wikipedia','var(--ax-wiki)'];
      if (/facebook|instagram|twitter|(^|\.)x\.com|youtube|tiktok|linkedin|threads/.test(d)) return ['Social','var(--ax-social)'];
      if (/reddit|quora|tripadvisor|trustpilot|yelp|glassdoor|skytrax|airlinequality|flyertalk|indeed/.test(d)) return ['Crowd','var(--ax-crowd)'];
      if (/reuters|\.gov|gov\.il|bloomberg|jpost|timesofisrael|globes|yahoo|investing\.com|tradingview|bbc|cnn|nytimes|forbes|haaretz|iata|news/.test(d)) return ['Expert','var(--ax-expert)'];
      return ['Other','var(--ax-notowned)','lite'];
    }
  }
};
